import React from 'react';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';

import { duration, easing, spring } from '../../theme/motion';
import { usePrefersReduced } from './useMotionPreference';

/** Vertical travel for the enter/exit slide (px). */
const SLIDE_Y = 12;

export interface TransitionProps {
  /**
   * Identity of the currently-rendered content. Changing it triggers an
   * exit-then-enter swap via `AnimatePresence mode="wait"`.
   */
  transitionKey: string;
  /**
   * Opt-in: smoothly tween the wrapper's HEIGHT between the outgoing and
   * incoming content so the page doesn't jump when the two keyed children
   * differ in height. Default `false` — when off, the primitive renders exactly
   * as it always has (no outer wrapper, no measuring), so existing consumers
   * (StageWrapper, the App.tsx page transition) are byte-for-byte unchanged.
   */
  animateHeight?: boolean;
  children?: React.ReactNode;
}

/**
 * Page/stage transition wrapper. Wraps content in
 * `<AnimatePresence mode="wait">` keyed by `transitionKey` so the outgoing
 * content animates out before the incoming content animates in (fade + small
 * vertical slide, `spring.gentle`).
 *
 * Reduced-motion branch: when `usePrefersReduced()` is true, the slide (`y`)
 * is dropped and only opacity is animated.
 *
 * When `animateHeight` is true, the whole `AnimatePresence` is wrapped in an
 * outer `m.div` whose height tweens to the currently-rendered content height
 * (measured with a `ResizeObserver`). Because `mode="wait"` leaves the inner
 * content briefly empty during the out→in gap, non-positive measurements are
 * ignored — the wrapper holds the outgoing height until the incoming content
 * mounts and is measured, avoiding a collapse-to-0 flash. Under reduced motion
 * the height is left at `auto` (no clipping, no tween).
 */
export const Transition: React.FC<TransitionProps> = ({
  transitionKey,
  animateHeight = false,
  children,
}) => {
  const prefersReduced = usePrefersReduced();

  const variants = prefersReduced
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : {
        initial: { opacity: 0, y: SLIDE_Y },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -SLIDE_Y },
      };

  const presence = (
    <AnimatePresence mode="wait">
      <m.div
        key={transitionKey}
        data-transition-key={transitionKey}
        initial={variants.initial}
        animate={variants.animate}
        exit={variants.exit}
        transition={spring.gentle}
      >
        {children}
      </m.div>
    </AnimatePresence>
  );

  // Plain path: identical to the original implementation. Also used under
  // reduced motion so height is never clipped or tweened.
  if (!animateHeight || prefersReduced) {
    return presence;
  }

  return <HeightAnimated transitionKey={transitionKey}>{presence}</HeightAnimated>;
};

/**
 * Outer wrapper that animates its height to fit the currently-rendered inner
 * content. Measures the inner wrapper with a `ResizeObserver` (skipped in
 * environments lacking it, falling back to `height: 'auto'`). Only positive
 * measurements update the tracked height, so the `mode="wait"` out→in gap
 * (when the inner content is momentarily empty, height 0) does not collapse
 * the wrapper.
 *
 * `overflow` is timed: `hidden` ONLY while the height is actively tweening (a
 * step swap is in flight) so the cross-fade/reveal can't spill, and `visible`
 * at rest so hover-scaled content near the wrapper edges (e.g. the homepage
 * `MotionCard`s that scale up on hover) is never clipped.
 */
const HeightAnimated: React.FC<{
  transitionKey: string;
  children: React.ReactNode;
}> = ({ transitionKey, children }) => {
  const innerRef = React.useRef<HTMLDivElement | null>(null);
  // `undefined` => not yet measured: render `height: 'auto'` so first paint is
  // natural and `initial={false}` prevents an open-from-0 animation.
  const [height, setHeight] = React.useState<number | undefined>(undefined);
  // Clip overflow only while the height is mid-tween; visible at rest so
  // hover-scaled cards aren't cut off. Defaults to false (rest) on first mount.
  const [isHeightAnimating, setIsHeightAnimating] = React.useState(false);
  // The first effect run is the initial mount, not a step swap — don't clip then.
  const didMountRef = React.useRef(false);

  // A change in `transitionKey` means a step swap has started: the incoming
  // content will be re-measured and the height will tween, so clip during it.
  React.useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setIsHeightAnimating(true);
  }, [transitionKey]);

  React.useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    if (typeof ResizeObserver === 'undefined') {
      // No measuring available — leave height at auto.
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const measured =
          entry.contentRect?.height ?? el.getBoundingClientRect().height;
        // Ignore non-positive measurements (the empty out→in gap) so the
        // wrapper holds the outgoing height until incoming content mounts.
        if (measured > 0) {
          setHeight(measured);
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <m.div
      initial={false}
      animate={{ height: height ?? 'auto' }}
      transition={{ duration: duration.base, ease: easing }}
      onAnimationComplete={() => setIsHeightAnimating(false)}
      style={{ overflow: isHeightAnimating ? 'hidden' : 'visible' }}
    >
      <div ref={innerRef}>{children}</div>
    </m.div>
  );
};

export default Transition;
